import type React from 'react';
import { useState } from 'react';

import { EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router-dom';
import { entryStatusConstants, policyConstants, tournamentEngine } from 'tods-competition-factory';

import { eventAtomFamily } from 'atoms/events';
import CreateAvoidanceOverlay from 'components/overlays/avoidance/CreateAvoidanceOverlay';
import { TOURNAMENT_ENGINE } from 'config/graphql/constants';
import { avoidanceConstantsArray } from 'constants/avoidance';
import { useSaveTournament } from 'hooks/tournament/useSaveTournament';
import { useTournamentContext } from 'hooks/tournament/useTournamentContext';
import {
  type GroupParticipantInterface,
  type PlainAvoidanceGroupInterface,
  type PlainAvoidanceRuleInterface,
} from 'typedefs/avoidance';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { isDoublesOrTeamChecked } from 'utils/avoidanceUtils';
import { participantName } from 'utils/participant';

const { UNPAIRED, UNGROUPED, WITHDRAWN } = entryStatusConstants;
const { POLICY_TYPE_AVOIDANCE } = policyConstants;

export interface AddAvoidanceContainerProps {
  policyDefinition?: any; // TODO: add type
  existingGroups: ParticipantInterface[];
  existingRules?: string[];
  isOpen?: boolean;
  readOnly?: boolean;
  onClose: () => void;
}

const CreateAvoidanceContainer: React.FC<React.PropsWithChildren<AddAvoidanceContainerProps>> = ({
  policyDefinition,
  existingGroups,
  existingRules,
  isOpen,
  readOnly,
  onClose,
}) => {
  const { tournament } = useTournamentContext();
  const { eventId } = useParams<{ eventId: string }>();
  const event = useAtomValue(eventAtomFamily(eventId));
  const [currentAvoidance, setCurrentAvoidance] = useState(policyDefinition?.avoidance);
  const { saveTournament } = useSaveTournament();
  const isDoubles = event.eventType === EventTypeEnum.DOUBLES;
  const isTeam = event.eventType === EventTypeEnum.TEAM;
  const groupParticipantsIds: string[] = (event.entries || [])
    .filter((entry) => ![UNPAIRED, UNGROUPED, WITHDRAWN].includes(entry.entryStatus))
    ?.map((entry) => entry.participantId);

  const filteredExistingGroups = existingGroups.map((group) => {
    if (isDoubles || isTeam) {
      const groupParticipants = tournament?.participants?.filter((participant) =>
        groupParticipantsIds.includes(participant.participantId),
      );
      const filteredGroupParticipants = groupParticipants.filter((p) => {
        const individualParticipantIds = p?.individualParticipantIds;
        return individualParticipantIds?.every((id) => group?.individualParticipantIds?.includes(id));
      });
      return {
        ...group,
        individualParticipantIds: filteredGroupParticipants?.map((p) => p.individualParticipantIds)?.flat(),
      };
    } else {
      const filteredIds = group?.individualParticipantIds?.filter((id) => groupParticipantsIds.includes(id));
      return { ...group, individualParticipantIds: filteredIds };
    }
  });

  const [groups, setGroups] = useState(filteredExistingGroups || []);

  const currentAvoidanceRules = avoidanceConstantsArray.map<PlainAvoidanceRuleInterface>((constant, index) => ({
    index: index,
    name: constant,
    checked: existingRules?.includes(constant) || false,
  }));
  const [avoidanceRules, setAvoidanceRules] = useState<PlainAvoidanceRuleInterface[]>(currentAvoidanceRules);
  const setRules = (rules: PlainAvoidanceRuleInterface[]) => {
    setAvoidanceRules(rules);
  };
  const preSelectedParticipantsIds = groups?.flatMap((group) => group?.individualParticipantIds);

  const groupParticipants: GroupParticipantInterface[] = tournament?.participants
    ?.filter((participant) => groupParticipantsIds.includes(participant.participantId))
    ?.map((participant) => {
      return {
        id: participant.participantId,
        name: participantName(participant),
        checked:
          isTeam || isDoubles
            ? isDoublesOrTeamChecked(participant, preSelectedParticipantsIds)
            : preSelectedParticipantsIds?.includes(participant.participantId),
        ...{ individualParticipantIds: participant?.individualParticipantIds },
      };
    });

  const handleOnOpen = () => {
    setRules(currentAvoidanceRules);
    setGroups(filteredExistingGroups);
    setCurrentAvoidance(policyDefinition?.avoidance);
  };
  const handleSetAvoidance = (avoidanceRules) => {
    const policyAttributes = [...new Set(avoidanceRules?.policyAttributes || [])];
    setCurrentAvoidance({
      ...currentAvoidance,
      policyAttributes,
    });
  };
  const createAvoidanceGroup = (
    groupName: string,
    individualParticipantIds: string[],
    selectedGroup?: PlainAvoidanceGroupInterface,
  ) => {
    const { participant } = tournamentEngine.createGroupParticipant({
      groupName: groupName,
      individualParticipantIds: individualParticipantIds,
    });

    const participantToUse = selectedGroup
      ? {
          ...participant,
          participantId: selectedGroup.id,
          individualParticipantIds: participant.individualParticipantIds,
          name: groupName,
          participantName: groupName,
        }
      : {
          ...participant,
          individualParticipantIds: participant.individualParticipantIds,
        };

    const oldGroupParticipants = currentAvoidance?.policyAttributes?.find(
      (attribute) => attribute.directive === 'groupParticipants',
    );
    const newIncludeIds = selectedGroup
      ? oldGroupParticipants?.includeIds || []
      : [...(oldGroupParticipants?.includeIds || []), participantToUse?.participantId];
    const groupPolicyAttribute = { directive: 'groupParticipants', includeIds: newIncludeIds };
    const existingPolicyAttributes =
      currentAvoidance?.policyAttributes?.filter((attribute) => attribute.directive !== 'groupParticipants') || [];
    const policyAttributes = existingPolicyAttributes.concat(groupPolicyAttribute);
    const avoidanceWithGroups = { ...currentAvoidance, policyAttributes };
    setCurrentAvoidance(avoidanceWithGroups);
    setGroups((prevState) => {
      return selectedGroup
        ? prevState?.map((group) =>
            selectedGroup && selectedGroup.id === group.participantId ? participantToUse : group,
          )
        : [...(prevState || []), participantToUse];
    });
  };
  const deleteAvoidanceGroup = (participantId: string) => {
    const oldPolicyAttributes = currentAvoidance?.policyAttributes?.find(
      (attribute) => attribute.directive === 'groupParticipants',
    );
    const updatedIncludeIds = oldPolicyAttributes?.includeIds?.filter((id) => id !== participantId);
    const updatedCurrentAvoidancePolicyAttributes = currentAvoidance?.policyAttributes
      ?.filter((attribute) => (attribute?.directive === 'groupParticipants' ? updatedIncludeIds?.length > 0 : true))
      .map((attribute) =>
        attribute?.directive === 'groupParticipants'
          ? {
              ...attribute,
              includeIds: updatedIncludeIds,
            }
          : attribute,
      );
    const updatedGroups = groups?.filter((group) => group?.participantId !== participantId);
    setCurrentAvoidance({
      ...currentAvoidance,
      policyAttributes: updatedCurrentAvoidancePolicyAttributes,
    });
    setGroups(updatedGroups);
  };
  const handleSaveAvoidance = async () => {
    const methods = [];
    if (existingGroups?.length > 0) {
      existingGroups.map((existingGroup) => {
        const newIndividualParticipants =
          groups?.find((newGroup) => newGroup.participantId === existingGroup.participantId)
            ?.individualParticipantIds || [];
        methods.push({
          method: 'modifyIndividualParticipantIds',
          params: {
            groupingParticipantId: existingGroup.participantId,
            individualParticipantIds: newIndividualParticipants,
          },
        });
        methods.push({
          method: 'modifyParticipantName',
          params: {
            participantId: existingGroup.participantId,
            participantName: existingGroup.participantName,
          },
        });
      });
    }

    const existingGroupIds = existingGroups?.map((group) => group.participantId);
    const newGroups = groups?.filter((group) => !existingGroupIds?.includes(group.participantId));
    if (newGroups && newGroups.length > 0) {
      methods.push({
        method: 'addParticipants',
        params: { participants: newGroups },
      });
    }
    methods.push({
      method: 'attachPolicies',
      params: {
        eventId: event.eventId,
        allowReplacement: true,
        policyDefinitions: {
          avoidance: currentAvoidance,
        },
      },
    });
    await saveTournament('createAvoidance', TOURNAMENT_ENGINE, methods);
  };
  const handleDeleteAvoidance = async () => {
    setCurrentAvoidance(null);
    setGroups(null);
    const uncheckedRules = avoidanceRules?.map((rule) => ({
      ...rule,
      checked: false,
    }));
    setAvoidanceRules(uncheckedRules);
    const methods = [
      {
        method: 'removePolicy',
        params: {
          eventId: event.eventId,
          policyType: POLICY_TYPE_AVOIDANCE,
        },
      },
    ];
    await saveTournament('deleteAvoidance', TOURNAMENT_ENGINE, methods);
  };
  return (
    <CreateAvoidanceOverlay
      avoidanceFromStorage={policyDefinition?.avoidance}
      createAvoidanceGroup={createAvoidanceGroup}
      currentAvoidance={currentAvoidance}
      currentRules={avoidanceRules}
      deleteAvoidance={handleDeleteAvoidance}
      deleteAvoidanceGroup={deleteAvoidanceGroup}
      existingGroups={groups}
      groupParticipants={groupParticipants}
      isDoubles={isDoubles}
      isOpen={isOpen}
      isTeam={isTeam}
      readOnly={readOnly}
      saveAvoidance={handleSaveAvoidance}
      setAvoidance={handleSetAvoidance}
      setRules={setRules}
      onClose={onClose}
      onOpen={handleOnOpen}
    />
  );
};

export default CreateAvoidanceContainer;
